import { useState, useMemo, useEffect, Fragment } from 'react'
import { motion } from 'framer-motion'
import {
  Drawer,
  DrawerContent,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { supabase } from '@/lib/supabase'
import type { EvidenceLog } from '@/types/database'
import { hapticSelection } from '@/lib/native'
import { playFireLoop, stopFireLoop } from '@/lib/native/sounds'

interface StreakModalProps {
  open: boolean
  onClose: () => void
  globalStreak: number
  yearlyLogs?: EvidenceLog[]
}

export function StreakModal({ open, onClose, globalStreak, yearlyLogs = [] }: StreakModalProps) {
  const [viewingMonth, setViewingMonth] = useState(new Date())
  const [restDays, setRestDays] = useState<Set<string>>(new Set())
  const [shieldDays, setShieldDays] = useState<Set<string>>(new Set())
  const [freshLogs, setFreshLogs] = useState<EvidenceLog[]>([])
  
  // Play fire loop when modal opens and user is on fire
  useEffect(() => {
    if (open && globalStreak > 0) {
      // Small delay for dramatic effect
      const timer = setTimeout(() => {
        playFireLoop(0.025) // Subtle ambient volume
      }, 300)
      
      return () => {
        clearTimeout(timer)
        stopFireLoop()
      }
    } else {
      stopFireLoop()
    }
  }, [open, globalStreak])

  // Fetch fresh data when modal opens
  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return
        
        // Fetch rest days
        const { data: restDaysData } = await supabase
          .from('rest_days')
          .select('rest_date')
          .eq('user_id', user.id)
        
        if (restDaysData) {
          setRestDays(new Set(restDaysData.map(rd => rd.rest_date)))
        }
        
        // Fetch shield usage
        const { data: shieldData } = await supabase
          .from('shield_usage')
          .select('protected_date')
          .eq('user_id', user.id)
        
        if (shieldData) {
          setShieldDays(new Set(shieldData.map(sd => sd.protected_date)))
        }
        
        // Fetch fresh completion logs
        const oneYearAgo = new Date()
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
        
        const { data: logsData } = await supabase
          .from('evidence_logs')
          .select('*')
          .eq('user_id', user.id)
          .gte('created_at', oneYearAgo.toISOString())
        
        if (logsData) {
          setFreshLogs(logsData as EvidenceLog[])
        }
      }
      
      fetchData()
    }
  }, [open])
  
  // Use fresh logs if available, otherwise fall back to props
  const activeLogs = freshLogs.length > 0 ? freshLogs : yearlyLogs
  
  // Get successful completions only
  const completedDates = useMemo(() => {
    const dates = new Set<string>()
    activeLogs.forEach(log => {
      if (
        log.ai_verdict === 'approved_gold' ||
        log.ai_verdict === 'approved_silver' ||
        log.ai_verdict === 'quick_complete'
      ) {
        const d = new Date(log.created_at)
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        dates.add(dateStr)
      }
    })
    return dates
  }, [activeLogs])
  
  // Find the first completion date
  const firstCompletionDate = useMemo((): Date | null => {
    let earliest: Date | null = null
    activeLogs.forEach(log => {
      if (
        log.ai_verdict === 'approved_gold' ||
        log.ai_verdict === 'approved_silver' ||
        log.ai_verdict === 'quick_complete'
      ) {
        const d = new Date(log.created_at)
        d.setHours(0, 0, 0, 0)
        if (!earliest || d < earliest) {
          earliest = d
        }
      }
    })
    return earliest
  }, [activeLogs])
  
  // Calculate stats for the viewing month
  const monthStats = useMemo(() => {
    const year = viewingMonth.getFullYear()
    const month = viewingMonth.getMonth()
    
    let daysCompleted = 0
    completedDates.forEach(dateStr => {
      const [dateYear, dateMonth] = dateStr.split('-').map(Number)
      if (dateYear === year && dateMonth === month + 1) {
        daysCompleted++
      }
    })
    
    return { daysCompleted }
  }, [viewingMonth, completedDates])
  
  // Get milestone message based on streak
  const getMilestoneMessage = () => {
    if (globalStreak === 0) return null
    
    const weeks = Math.floor(globalStreak / 7)
    
    if (weeks >= 52) return `You've kept a Perfect Streak for ${weeks} weeks! Legendary!`
    if (weeks >= 12) return `You've kept a Perfect Streak for ${weeks} weeks! Amazing!`
    if (weeks >= 4) return `You've kept a Perfect Streak for ${weeks} weeks! Wow!`
    if (weeks >= 2) return `You've kept a Perfect Streak for ${weeks} weeks! Keep going!`
    if (weeks >= 1) return `You've completed a full week! Great start!`
    if (globalStreak >= 3) return `${globalStreak} days strong! You're building momentum!`
    return `Great start! Keep the streak alive!`
  }
  
  // Detect "grace days" — single missed days flanked by covered days
  // These are days where the "Don't Miss Twice" rule preserved the streak
  const graceDays = useMemo(() => {
    const grace = new Set<string>()
    if (completedDates.size === 0) return grace
    
    // Build a set of all covered dates (completions + rest + shield)
    const coveredDates = new Set<string>([...completedDates, ...restDays, ...shieldDays])
    
    // For each past date, check if it's a "grace day":
    // Not covered, but the day before AND day after are both covered
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    // Check dates within the last year
    const checkDate = new Date(today)
    checkDate.setFullYear(checkDate.getFullYear() - 1)
    
    while (checkDate < today) {
      checkDate.setDate(checkDate.getDate() + 1)
      const dateStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`
      
      if (coveredDates.has(dateStr)) continue // Not a gap
      
      // Check day before
      const prevDate = new Date(checkDate)
      prevDate.setDate(prevDate.getDate() - 1)
      const prevStr = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}-${String(prevDate.getDate()).padStart(2, '0')}`
      
      // Check day after
      const nextDate = new Date(checkDate)
      nextDate.setDate(nextDate.getDate() + 1)
      const nextStr = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`
      
      // Grace day: both neighbors are covered, OR day before is covered and this is yesterday (today isn't over)
      const isYesterday = checkDate.getTime() === new Date(today.getTime() - 86400000).setHours(0, 0, 0, 0)
      
      if (coveredDates.has(prevStr) && (coveredDates.has(nextStr) || isYesterday)) {
        grace.add(dateStr)
      }
    }
    
    return grace
  }, [completedDates, restDays, shieldDays])

  // Generate calendar grid
  const calendarGrid = useMemo(() => {
    const year = viewingMonth.getFullYear()
    const month = viewingMonth.getMonth()
    
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startDayOfWeek = firstDay.getDay()
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const weeks: { day: number | null; isCompleted: boolean; isToday: boolean; isFuture: boolean; isRestDay: boolean; isShieldDay: boolean; isBeforeStart: boolean; isGraceDay: boolean }[][] = []
    let currentWeek: { day: number | null; isCompleted: boolean; isToday: boolean; isFuture: boolean; isRestDay: boolean; isShieldDay: boolean; isBeforeStart: boolean; isGraceDay: boolean }[] = []
    
    // Fill empty days before start of month
    for (let i = 0; i < startDayOfWeek; i++) {
      currentWeek.push({ day: null, isCompleted: false, isToday: false, isFuture: false, isRestDay: false, isShieldDay: false, isBeforeStart: false, isGraceDay: false })
    }
    
    // Fill days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      date.setHours(0, 0, 0, 0)
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const isCompleted = completedDates.has(dateStr)
      const isRestDay = restDays.has(dateStr)
      const isShieldDay = shieldDays.has(dateStr)
      const isGraceDay = graceDays.has(dateStr)
      const isToday = date.getFullYear() === today.getFullYear() && 
                      date.getMonth() === today.getMonth() && 
                      date.getDate() === today.getDate()
      const isFuture = date > today
      const isBeforeStart = firstCompletionDate !== null ? date.getTime() < firstCompletionDate.getTime() : true
      
      currentWeek.push({ day, isCompleted, isToday, isFuture, isRestDay, isShieldDay, isBeforeStart, isGraceDay })
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek)
        currentWeek = []
      }
    }
    
    // Fill remaining days
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push({ day: null, isCompleted: false, isToday: false, isFuture: false, isRestDay: false, isShieldDay: false, isBeforeStart: false, isGraceDay: false })
      }
      weeks.push(currentWeek)
    }
    
    return weeks
  }, [viewingMonth, completedDates, restDays, shieldDays, firstCompletionDate, graceDays])
  
  // Check if a week has all days completed
  const getWeekCompletionStatus = (week: typeof calendarGrid[0]) => {
    const validDays = week.filter(d => d.day !== null && !d.isFuture)
    if (validDays.length === 0) return 'empty'
    const allCompleted = validDays.every(d => d.isCompleted)
    if (allCompleted) return 'full'
    return 'none'
  }
  
  const navigateMonth = (delta: number) => {
    hapticSelection()
    setViewingMonth(prev => {
      const newDate = new Date(prev)
      newDate.setMonth(newDate.getMonth() + delta)
      return newDate
    })
  }
  
  const monthName = viewingMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const milestoneMessage = getMilestoneMessage()

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DrawerContent className="max-h-[90vh]">
        {/* Decorative Background */}
        <div className={`absolute top-0 inset-x-0 h-24 bg-gradient-to-b ${globalStreak > 0 ? 'from-orange-500/10' : 'from-sky-500/10'} to-transparent pointer-events-none rounded-t-[20px]`} />
        
        <div className="p-4 space-y-3 relative z-10 overflow-y-auto">
          {/* Header - Streak Display */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center pt-2"
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <FluentEmoji emoji="🏆" size={16} />
              <span className="text-orange-500 text-[10px] font-bold tracking-[0.2em] uppercase">
                Your Streak
              </span>
              <FluentEmoji emoji="🏆" size={16} />
            </div>
            
            <div className="relative inline-flex flex-col items-center">
              {/* Glowing background */}
              <div className={`absolute inset-0 ${globalStreak > 0 ? 'bg-orange-500/20' : 'bg-sky-500/20'} blur-3xl rounded-full scale-150`} />
              
              <motion.div
                animate={globalStreak > 0 ? { 
                  scale: [1, 1.08, 1],
                  rotate: [0, 3, -3, 0]
                } : {}}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-10"
              >
                <FluentEmoji emoji={globalStreak > 0 ? "🔥" : "🧊"} size={48} animated={globalStreak > 0} />
              </motion.div>
              
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', damping: 12, delay: 0.2 }}
                className="relative z-10 text-center"
              >
                <div className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter tabular-nums leading-none">
                  {globalStreak}
                </div>
                <div className={`${globalStreak > 0 ? 'text-orange-500' : 'text-sky-400'} text-[10px] font-bold tracking-[0.15em] uppercase mt-0.5`}>
                  {globalStreak > 0 ? 'Day Streak' : 'No Streak'}
                </div>
              </motion.div>
            </div>
          </motion.div>
          
          {/* Milestone Message */}
          {milestoneMessage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 flex items-center gap-3"
            >
              <FluentEmoji emoji="🏆" size={24} />
              <p className="text-slate-600 dark:text-slate-300 text-xs leading-snug">
                {milestoneMessage.split('Perfect Streak').map((part, i) => (
                  i === 0 ? part : <Fragment key={i}><span className="text-orange-400 font-bold">Perfect Streak</span>{part}</Fragment>
                ))}
              </p>
            </motion.div>
          )}
          
          {/* Calendar Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-100/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl p-4"
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateMonth(-1)}
                  className="text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 w-7 h-7 rounded-md"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wide px-2">{monthName}</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateMonth(1)}
                  className="text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 w-7 h-7 rounded-md"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                <span className="text-orange-500 dark:text-orange-400 font-bold">{monthStats.daysCompleted}</span> completed
              </div>
            </div>
            
            {/* Calendar Grid */}
            <div className="space-y-0.5">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                  <div key={i} className="text-center text-xs text-slate-500 font-bold uppercase">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Weeks */}
              {calendarGrid.map((week, weekIndex) => {
                const weekStatus = getWeekCompletionStatus(week)
                const isFullWeek = weekStatus === 'full'
                
                return (
                  <div
                    key={weekIndex}
                    className={`grid grid-cols-7 gap-2 py-1 rounded-md transition-all ${
                      isFullWeek ? 'bg-orange-500/10' : ''
                    }`}
                  >
                    {week.map((dayInfo, dayIndex) => {
                      const isMissedDay = !dayInfo.isCompleted && !dayInfo.isRestDay && !dayInfo.isShieldDay &&
                                          !dayInfo.isGraceDay && !dayInfo.isFuture && dayInfo.day !== null && 
                                          !dayInfo.isToday && !dayInfo.isBeforeStart
                      
                      const isInactiveDay = dayInfo.isBeforeStart && !dayInfo.isCompleted && 
                                            !dayInfo.isRestDay && !dayInfo.isShieldDay && !dayInfo.isFuture && dayInfo.day !== null
                      
                      return (
                        <div
                          key={dayIndex}
                          className={`
                            relative aspect-square flex items-center justify-center text-base font-bold rounded-lg transition-all
                            ${dayInfo.day === null ? 'opacity-0' : ''}
                            ${dayInfo.isToday && !dayInfo.isCompleted && !dayInfo.isRestDay && !dayInfo.isShieldDay ? 'ring-2 ring-orange-500 text-orange-400' : ''}
                            ${dayInfo.isFuture && !dayInfo.isRestDay && !dayInfo.isShieldDay ? 'text-slate-600' : ''}
                            ${dayInfo.isCompleted ? 'text-white bg-orange-500' : ''}
                            ${dayInfo.isRestDay ? 'text-white bg-emerald-500' : ''}
                            ${dayInfo.isShieldDay ? 'text-white bg-violet-500' : ''}
                            ${dayInfo.isGraceDay ? 'bg-amber-500/30 text-amber-500 dark:text-amber-400' : ''}
                            ${isMissedDay ? 'bg-sky-500/30 text-sky-400' : ''}
                            ${isInactiveDay ? 'text-slate-600' : ''}
                          `}
                        >
                          {dayInfo.isRestDay ? (
                            <FluentEmoji emoji="🏝️" size={24} />
                          ) : dayInfo.isShieldDay ? (
                            <FluentEmoji emoji="🛡️" size={24} />
                          ) : dayInfo.isGraceDay ? (
                            <FluentEmoji emoji="💛" size={20} />
                          ) : (
                            dayInfo.day
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
            
            {/* Legend */}
            <div 
              className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-slate-200 dark:border-slate-800 flex-wrap"
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-orange-500" />
                <span className="text-sm text-slate-500 dark:text-slate-400">Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md ring-2 ring-orange-500" />
                <span className="text-sm text-slate-500 dark:text-slate-400">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-amber-500/30 flex items-center justify-center">
                  <FluentEmoji emoji="💛" size={12} />
                </div>
                <span className="text-sm text-slate-500 dark:text-slate-400">Grace</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-sky-500/30" />
                <span className="text-sm text-slate-500 dark:text-slate-400">Missed</span>
              </div>
              <div className="flex items-center gap-2">
                <FluentEmoji emoji="🏝️" size={20} />
                <span className="text-sm text-slate-500 dark:text-slate-400">Rest</span>
              </div>
              <div className="flex items-center gap-2">
                <FluentEmoji emoji="🛡️" size={20} />
                <span className="text-sm text-slate-500 dark:text-slate-400">Shielded</span>
              </div>
            </div>
            
            {/* "Don't Miss Twice" explainer */}
            <div 
              className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800"
              style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
            >
              <div className="flex items-center justify-center gap-1.5">
                <FluentEmoji emoji="💡" size={14} />
                <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                  Don&apos;t Miss Twice
                </span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      onClick={() => hapticSelection()}
                      className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors"
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="top" className="w-64 p-3">
                    <p className="text-xs text-amber-700/80 dark:text-amber-400/70 leading-relaxed text-center">
                      One off day won&apos;t break your streak — it becomes a grace day. But missing two days in a row resets it. The first mistake is never what ruins a habit; it&apos;s the spiral that follows.
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </motion.div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
